import React, { useState, useEffect, useContext } from 'react';
import { useIntl } from 'react-intl';
import { Loader, ErrorComponent } from 'shared-components';
import { deleteHTTP, get, post, put } from 'shared-components/src/utils/http';
import Heading from '../../../../components/Heading';
import Title from '../../../../components/Title';
import Alert from '../../../../components/Alert';
import Button from '../../../../components/Button';
import Card from '../../../../components/Card';
import Text from '../../../../components/Text';
import useAlert from '../../../../hooks/useAlert';
import ExternalTransferDirectsendPullForm from './components/ExternalTransferDirectsendPullForm';
import { TitleContainer, TextContainer } from './styles';
import { HeadingWrapper } from '../../styles';
import CreateExternalDirectsendAccountV2Modal from '../../components/CreateExternalDirectsendAccountV2Modal';
import EditExternalDirectsendAccountV2Modal from '../../components/EditExternalDirectsendAccountV2Modal';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import ThreeDSecureModal from '../../components/ThreeDSecureModal';
import TransferModal from '../../components/TransferModal';
import PartialApprovalModal from '../../components/PartialApprovalModal';
import { directsendPullComponentName, updateFeeAmounts } from './utils';
import { ProgramConfigContext } from '../../../../contexts/ProgramConfigContext';

const directsendPull = () => {
  const intl = useIntl();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useAlert();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showThreeDSecureModal, setShowThreeDSecureModal] = useState(false);
  const [showPartialApprovalModal, setShowPartialApprovalModal] = useState(false);
  const [externalAccounts, setExternalAccounts] = useState([]);
  const [pullParams, setPullParams] = useState(null);
  const [threeDSecureCallbacks, setThreeDSecureCallbacks] = useState({});
  const [feeAmounts, setFeeAmounts] = useState({
    create: '',
  });
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [partiallyApprovedTransfer, setPartiallyApprovedTransfer] = useState(null);
  const configs = useContext(ProgramConfigContext);

  useEffect(() => {
    const fetchExternalAccounts = async () => {
      try {
        const [externalAccountsData] = await Promise.all([
          get(
            '/api/v2/cardholder_portal/cardholder/external_accounts?type=personal_source_account'
          ),
        ]);

        setExternalAccounts(externalAccountsData.data);
      } catch (e) {
        setError({ type: 'danger', message: e });
      }
      setLoading(false);
    };

    const fetchProgramFeeConfig = async () => {
      try {
        const res = await get(
          '/api/v1/cardholder_portal/cardholder/fee_configs'
        );
        const fees = updateFeeAmounts(res.data);
        setFeeAmounts(fees);
      } catch (e) {
        setError(e);
      }
    };

    fetchProgramFeeConfig();
    fetchExternalAccounts();
  }, []);

  const page = 'directsend-pull';

  const editExternalAccount = account => {
    setSelectedAccount(account);
    setShowEditModal(true);
  };

  const deleteExternalAccount = account => {
    setSelectedAccount(account);
    setShowDeleteModal(true);
  };

  const closeConfirmDeleteModal = e => {
    e.preventDefault();
    setShowDeleteModal(false);
    setAlert(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await Promise.all([
        deleteHTTP(
          `/api/v2/cardholder_portal/cardholder/external_accounts/${selectedAccount.id}`
        ),
      ]);
      setExternalAccounts(
        externalAccounts.filter(acc => acc.id !== selectedAccount.id)
      );
      setAlert({
        type: 'success',
        message: intl.messages[`${page}-account-deleted-alert`],
        messageFor: 'account-deleted',
        component: directsendPullComponentName,
      });
      setLoading(false);
      setShowDeleteModal(false);
    } catch (e) {
      setAlert({
        type: 'danger',
        message: e.message,
        messageFor: 'delete-error',
        component: directsendPullComponentName,
      });
      setLoading(false);
      setShowDeleteModal(false);
    }
  };

  const renderAlerts = component => (
    alert &&
      component === alert.component && (
      <Alert
        type={alert.type}
        alert={alert.message}
        alertFor={alert.messageFor}
        page={alert.page || page}
      />
    )
  );

  const renderNewContactButton = () => (
    <Button
      buttonText={intl.messages[`${page}-new-contact-button`]}
      buttonFor="new-contact"
      page={page}
      onClick={e => {
        e.preventDefault();
        setShowCreateModal(true);
      }}
      type="primary"
    />
  );

  const createPullTransfer = async params => {
    setLoading(true);
    try {
      const response = await post(
        '/api/v2/cardholder_portal/cardholder/external_transfers',
        params
      );

      // Check if the transfer was partially approved
      if (response.data && response.data.transfer && response.data.transfer.status === 'partially_approved') {
        setPartiallyApprovedTransfer(response.data.transfer);
        setShowPartialApprovalModal(true);
        setLoading(false);
        setShowConfirmModal(false);
      } else {
        // set success alert for fully approved transfers
        setAlert({
          type: 'success',
          message: intl.messages[`${page}-transfer-created-alert`],
          messageFor: 'transfer-created',
          component: directsendPullComponentName,
        });
        setLoading(false);
        setShowConfirmModal(false);
      }
    } catch (e) {
      setAlert({
        type: 'danger',
        message: e.message,
        messageFor: 'transfer-error',
        component: directsendPullComponentName,
      });
      setLoading(false);
      setShowConfirmModal(false);
    }
  };

  const handle3DSecureError = error => {
    setAlert({
      type: 'danger',
      message: error.message,
      messageFor: '3d-secure-error',
      component: directsendPullComponentName,
    });
    setLoading(false);
    setShowThreeDSecureModal(false);
  };

  const createPullTransferWith3DSecure = async () => {
    setShowThreeDSecureModal(true);
    setShowConfirmModal(false);
    try {
      await new Promise(resolve => {
        const handleSuccess = async params => {
          setShowThreeDSecureModal(false);
          setAlert({
            type: 'info',
            message: intl.messages[`${page}-3ds-authenticated-alert`],
            messageFor: '3d-secure-success',
            component: directsendPullComponentName,
          });
          setTimeout(async () => {
            await createPullTransfer(params);
          }, 1000);
          resolve();
        };

        const handleError = e => {
          handle3DSecureError(e);
        };
        setThreeDSecureCallbacks({
          onSuccess: handleSuccess,
          onError: handleError,
        });
      });
    } catch (e) {
      handle3DSecureError(e);
    }
  };

  const acceptPartialApproval = async transferId => {
    setLoading(true);
    try {
      await put(
        `/api/v2/cardholder_portal/cardholder/external_transfers/${transferId}/accept`
      );

      setAlert({
        type: 'success',
        message: intl.messages[`${page}-partial-approval-accepted-alert`],
        messageFor: 'partial-approval-accepted',
        component: directsendPullComponentName,
      });
      setLoading(false);
      setShowPartialApprovalModal(false);
    } catch (e) {
      setAlert({
        type: 'danger',
        message: e.message,
        messageFor: 'partial-approval-error',
        component: directsendPullComponentName,
      });
      setLoading(false);
      setShowPartialApprovalModal(false);
    }
  };

  const rejectPartialApproval = async transferId => {
    setLoading(true);
    try {
      await put(
        `/api/v2/cardholder_portal/cardholder/external_transfers/${transferId}/reject`
      );

      setAlert({
        type: 'info',
        message: intl.messages[`${page}-partial-approval-rejected-alert`],
        messageFor: 'partial-approval-rejected',
        component: directsendPullComponentName,
      });
      setLoading(false);
      setShowPartialApprovalModal(false);
    } catch (e) {
      setAlert({
        type: 'danger',
        message: e.message,
        messageFor: 'partial-approval-error',
        component: directsendPullComponentName,
      });
      setLoading(false);
      setShowPartialApprovalModal(false);
    }
  };

  const createPullTransferWrapper = async params => {
    if (configs.require_3ds_external_transfers_v2) {
      await createPullTransferWith3DSecure(params);
    } else {
      await createPullTransfer(params);
    }
  };

  if (error) return <ErrorComponent />;
  if (loading) return <Loader />;

  // Otherwise, render the regular page
  return (
    <>
      <HeadingWrapper>
        <Heading page={page} heading={intl.messages[`${page}-heading`]} />
      </HeadingWrapper>
      <Card>
        {externalAccounts.length === 0 ? (
          <>
            {renderAlerts(directsendPullComponentName)}
            <TitleContainer>
              <Title
                title={intl.messages[`${page}-form-title`]}
                titleFor="form"
                page={page}
              />
              {renderNewContactButton()}
            </TitleContainer>
            <br />
            <TextContainer>
              <Alert
                type="info"
                alert={intl.messages[`${page}-add-contact-alert`]}
                alertFor="add-contact"
                page={page}
              />
            </TextContainer>
          </>
        ) : (
          <>
            {renderAlerts(directsendPullComponentName)}
            <TitleContainer>
              <Title
                title={intl.messages[`${page}-form-title`]}
                titleFor="form"
                page={page}
              />
              {renderNewContactButton()}
            </TitleContainer>
            <TextContainer>
              <Text
                page={page}
                textFor="form"
                text={intl.messages[`${page}-form-text`]}
              />
            </TextContainer>
            <ExternalTransferDirectsendPullForm
              setPullParams={setPullParams}
              setShowConfirmModal={setShowConfirmModal}
              disableSubmit={externalAccounts.length === 0}
              externalAccounts={externalAccounts}
              page={page}
              editExternalAccount={editExternalAccount}
              deleteExternalAccount={deleteExternalAccount}
            />
          </>
        )}
      </Card>
      {showCreateModal && (
        <CreateExternalDirectsendAccountV2Modal
          toggleModal={setShowCreateModal}
          setSuccess={setAlert}
          setExternalAccounts={setExternalAccounts}
          page={page}
        />
      )}
      {showConfirmModal && (
        <TransferModal
          toggleModal={setShowConfirmModal}
          confirmCallback={createPullTransferWrapper}
          params={pullParams}
          feeAmount={feeAmounts.create}
          type="create"
          description="Text for the total amount of fee applied for a successful transfer"
          page={page}
        />
      )}
      {showEditModal && (
        <EditExternalDirectsendAccountV2Modal
          toggleModal={setShowEditModal}
          setSuccess={setAlert}
          setExternalAccounts={setExternalAccounts}
          page={page}
          id={selectedAccount.id}
        />
      )}
      {showDeleteModal && (
        <ConfirmDeleteModal
          toggleModal={closeConfirmDeleteModal}
          loading={loading}
          alert={alert}
          deleteItemValue={selectedAccount.id}
          deleteItemName={selectedAccount.name}
          deleteCallback={handleDelete}
          page={page}
        />
      )}
      {showPartialApprovalModal && (
        <PartialApprovalModal
          acceptCallback={acceptPartialApproval}
          rejectCallback={rejectPartialApproval}
          transfer={partiallyApprovedTransfer}
          page={page}
        />
      )}
      {showThreeDSecureModal && (
        <ThreeDSecureModal
          toggleModal={setShowThreeDSecureModal}
          onSuccess={threeDSecureCallbacks.onSuccess}
          onError={threeDSecureCallbacks.onError}
          params={pullParams}
          page={page}
        />
      )}
    </>
  );
};

export default directsendPull;
